import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    cancelFailureReason,
    cancelOutcome,
    cancelPayload,
    parseScheduledSends,
} from '../../helpers/scheduledSends'
import { formatIndiaTime } from '../../helpers/sendSchedule'
import { errorMessage } from '../../services/axios/errorMessage'
import PipelineService from '../../services/pipelineService'
import { TEXT } from '../../theme/tokens'

// The times listed in the confirmation; the rest are counted.
const MAX_LISTED = 8

/** "1 send" or "3 sends". */
const sendsText = (n) => `${n} send${n === 1 ? '' : 's'}`

/**
 * Delete a pipeline, first cancelling the future sends scheduled for its
 * event type.
 *
 * A pipeline delete leaves scheduled sends in place (they are step-function
 * executions behind active bulk-log rows), so, as the old app did, the dialog
 * looks them up first (GET /comms/v2/get_schedule_comms). With none it asks
 * the plain confirmation; with some it lists their times in India time and
 * asks again. Confirming cancels them one at a time (POST
 * /comms/v2/disable_scheduled_comms), stops at the first failure with the
 * service's reason, and deletes the pipeline only when every cancel
 * succeeded. Open while `pipeline` is set.
 */
const DeletePipelineDialog = ({ pipeline, onClose, onDeleted }) => {
    const dispatch = useDispatch()
    // checking | checkFailed | confirm | working | failed
    const [step, setStep] = useState('checking')
    const [sends, setSends] = useState([])
    const [progress, setProgress] = useState('')
    // What failed (`check`, `cancel` or `delete`) and why.
    const [failure, setFailure] = useState({ at: '', message: '' })
    // How many sends this attempt cancelled before a failure.
    const [cancelled, setCancelled] = useState(0)
    // Bumped by every check and every close: an answer to an older one is
    // ignored, so a slow check cannot reopen a dialog closed meanwhile.
    const attempt = useRef(0)

    const name = pipeline?.pipeline_name || 'this pipeline'
    const eventType = pipeline?.event_type ?? ''

    /** Look up the event type's scheduled sends (none to look up without an event type). */
    const check = () => {
        attempt.current += 1
        const mine = attempt.current
        setFailure({ at: '', message: '' })
        setCancelled(0)
        if (!eventType) {
            setSends([])
            setStep('confirm')
            return
        }
        setStep('checking')
        dispatch(PipelineService.getScheduledSends(eventType)).then((resp) => {
            if (mine !== attempt.current) return
            if (resp.error) {
                setFailure({
                    at: 'check',
                    message: errorMessage({ data: resp.payload }),
                })
                setStep('checkFailed')
                return
            }
            setSends(parseScheduledSends(resp.payload?.data, { eventType }))
            setStep('confirm')
        })
    }

    useEffect(() => {
        if (pipeline) check()
        else attempt.current += 1
    }, [pipeline?.id])

    /** Delete the pipeline itself; `after` says what already happened, for a failure message. */
    const deletePipeline = async (after = '') => {
        setStep('working')
        setProgress('Deleting the pipeline…')
        const resp = await dispatch(PipelineService.deletePipeline(pipeline.id))
        if (resp.error) {
            setFailure({
                at: 'delete',
                message: `The pipeline was not deleted: ${errorMessage({
                    data: resp.payload,
                })}${after}`,
            })
            setStep('failed')
            return
        }
        onDeleted(pipeline)
    }

    /** Cancel each scheduled send in turn; delete only when all were cancelled. */
    const cancelThenDelete = async (toCancel) => {
        setStep('working')
        for (let i = 0; i < toCancel.length; i += 1) {
            const send = toCancel[i]
            setProgress(
                `Cancelling scheduled send ${i + 1} of ${toCancel.length}…`
            )
            // Sequential on purpose: the first failure stops the rest.
            // eslint-disable-next-line no-await-in-loop
            const resp = await dispatch(
                PipelineService.cancelScheduledSend(cancelPayload(send))
            )
            const outcome = resp.error
                ? { ok: false, reason: cancelFailureReason(resp.payload) }
                : cancelOutcome(resp.payload?.data)
            if (!outcome.ok) {
                setCancelled(i)
                setFailure({
                    at: 'cancel',
                    message: `The send at ${formatIndiaTime(
                        send.eventTime
                    )} was not cancelled: ${outcome.reason}`,
                })
                setStep('failed')
                return
            }
        }
        await deletePipeline(
            toCancel.length
                ? ` Its ${sendsText(
                      toCancel.length
                  )} scheduled for ${eventType} ${
                      toCancel.length === 1 ? 'was' : 'were'
                  } cancelled.`
                : ''
        )
    }

    // Nothing may close the dialog halfway through cancelling and deleting.
    const working = step === 'working'
    const listed = sends.slice(0, MAX_LISTED)

    return (
        <Dialog
            open={!!pipeline}
            onClose={working ? undefined : onClose}
            aria-labelledby="delete-pipeline-title"
        >
            <DialogTitle id="delete-pipeline-title">
                {`Delete ${name}?`}
            </DialogTitle>
            <DialogContent>
                {(step === 'checking' || working) && (
                    <Box
                        role="status"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                        <CircularProgress size={16} />
                        <Typography variant="body2" sx={{ color: TEXT.muted }}>
                            {step === 'checking'
                                ? `Checking for sends scheduled on ${eventType}…`
                                : progress}
                        </Typography>
                    </Box>
                )}
                {step === 'confirm' && sends.length === 0 && (
                    <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                        It stops sending immediately, and the delete cannot be
                        undone.
                    </Typography>
                )}
                {step === 'confirm' && sends.length > 0 && (
                    <>
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.secondary, mb: 1.5 }}
                        >
                            {`${sendsText(sends.length)} ${
                                sends.length === 1 ? 'is' : 'are'
                            } scheduled for ${eventType}. Deleting cancels ${
                                sends.length === 1 ? 'it' : 'them'
                            } first, then deletes the pipeline; the delete cannot be undone.`}
                        </Typography>
                        <Typography
                            variant="overline"
                            sx={{ color: TEXT.muted }}
                        >
                            Scheduled sends
                        </Typography>
                        <Box
                            component="ul"
                            aria-label="Scheduled sends"
                            sx={{ m: 0, pl: 2.5, mb: 1.5 }}
                        >
                            {listed.map((send) => (
                                <Typography
                                    component="li"
                                    variant="body2"
                                    key={`${send.eventId}-${send.eventTime}`}
                                    sx={{ color: TEXT.primary }}
                                >
                                    {formatIndiaTime(send.eventTime)}
                                </Typography>
                            ))}
                            {sends.length > listed.length && (
                                <Typography
                                    component="li"
                                    variant="body2"
                                    sx={{ color: TEXT.muted }}
                                >
                                    {`and ${sends.length - listed.length} more`}
                                </Typography>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ color: TEXT.muted }}>
                            A scheduled send belongs to the event type, so it is
                            cancelled for every pipeline on it.
                        </Typography>
                    </>
                )}
                {step === 'checkFailed' && (
                    <>
                        <ErrorBanner
                            message={`The scheduled sends could not be checked: ${failure.message}`}
                        />
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.secondary, mt: 1.5 }}
                        >
                            Deleting now leaves any send scheduled for this
                            event type in place.
                        </Typography>
                    </>
                )}
                {step === 'failed' && (
                    <>
                        <ErrorBanner message={failure.message} />
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.secondary, mt: 1.5 }}
                        >
                            {failure.at === 'delete'
                                ? 'Try again to retry the delete.'
                                : `Nothing was deleted.${
                                      cancelled
                                          ? ` ${sendsText(
                                                cancelled
                                            )} before it ${
                                                cancelled === 1 ? 'was' : 'were'
                                            } cancelled.`
                                          : ''
                                  } Try again to check the sends still scheduled.`}
                        </Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={working}>
                    {step === 'failed' ? 'Close' : 'Cancel'}
                </Button>
                {(step === 'checkFailed' || step === 'failed') && (
                    <Button variant="outlined" onClick={check}>
                        Try again
                    </Button>
                )}
                {step === 'checkFailed' && (
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => deletePipeline()}
                    >
                        Delete anyway
                    </Button>
                )}
                {step === 'confirm' && (
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => cancelThenDelete(sends)}
                    >
                        {sends.length
                            ? `Cancel ${sendsText(sends.length)} and delete`
                            : 'Delete'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

DeletePipelineDialog.propTypes = {
    // The pipeline row to delete; the dialog is closed without one.
    pipeline: PropTypes.shape({
        id: PropTypes.string,
        pipeline_name: PropTypes.string,
        event_type: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    // Called with the row once it is deleted.
    onDeleted: PropTypes.func.isRequired,
}

export default DeletePipelineDialog
